// SettingsPanel — post-onboarding settings management.
//
// Surfaced as a modal overlay reachable from the TopBar gear icon.
// Edits the same UserSettings IDB record as OnboardingWizard, but
// without the multi-step ceremony — power-user view.

import { useCallback, useState } from 'react';

import { PROVIDER_REGISTRY, type ProviderSlug } from '../core/providers';
import { useSettings } from '../state/settings';

interface SettingsPanelProps {
  onClose: () => void;
}

export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const settings = useSettings((s) => s.settings);
  const patch = useSettings((s) => s.patch);
  const reset = useSettings((s) => s.reset);

  const [displayName, setDisplayName] = useState(settings.displayName);
  const [role, setRole] = useState(settings.role);
  const [providerSlug, setProviderSlug] = useState<ProviderSlug>(settings.providerSlug);
  const [apiKey, setApiKey] = useState(settings.apiKey);
  const [model, setModel] = useState(settings.model);
  const [saving, setSaving] = useState(false);

  const meta = PROVIDER_REGISTRY.find((p) => p.slug === providerSlug)!;

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      await patch({
        displayName: displayName.trim(),
        role: role.trim(),
        providerSlug,
        apiKey: apiKey.trim(),
        model: model.trim(),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }, [displayName, role, providerSlug, apiKey, model, patch, onClose]);

  const onReset = useCallback(async () => {
    if (!window.confirm('清晒設定（包括 API key）？呢個 device 會跳返 onboarding。')) {
      return;
    }
    await reset();
    onClose();
  }, [reset, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6 py-10 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-panel-title"
    >
      <div
        className="w-full max-w-[560px] rounded-xl border border-[#222230] bg-[#0d0d12] p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-6 flex items-baseline justify-between">
          <h2 id="settings-panel-title" className="text-[16px] font-semibold tracking-tight text-[#F0F0F5]">
            設定
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[18px] text-neutral-500 hover:text-neutral-200"
            aria-label="關閉"
          >
            ✕
          </button>
        </header>

        <div className="space-y-5">
          <Field label="你叫咩">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-md border border-[#222230] bg-[#050508] px-3 py-2 text-[13px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
            />
          </Field>

          <Field label="角色 / 背景">
            <textarea
              value={role}
              onChange={(e) => setRole(e.target.value)}
              rows={2}
              className="w-full resize-y rounded-md border border-[#222230] bg-[#050508] px-3 py-2 text-[13px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
            />
          </Field>

          <Field label="LLM provider">
            <div className="flex gap-2">
              {PROVIDER_REGISTRY.map((p) => {
                const selected = p.slug === providerSlug;
                return (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => setProviderSlug(p.slug)}
                    className={`flex-1 rounded-md border px-3 py-2 text-[12px] transition-colors ${
                      selected
                        ? 'border-[#8AFF45] bg-[#8AFF45]/10 text-[#8AFF45]'
                        : 'border-[#222230] bg-[#050508] text-neutral-300 hover:border-[#333348]'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={`Model (default: ${meta.defaultModel})`}>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={meta.defaultModel}
              className="w-full rounded-md border border-[#222230] bg-[#050508] px-3 py-2 font-mono text-[12px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
            />
            <p className="mt-1 text-[10.5px] text-neutral-600">
              留空用 default。換 model 嘅時候喺呢度填佢個 ID。
            </p>
          </Field>

          <Field label="API key">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="w-full rounded-md border border-[#222230] bg-[#050508] px-3 py-2 font-mono text-[12px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
            />
            <p className="mt-1 text-[10.5px] text-neutral-600">
              Key 儲喺呢部 device 嘅 IndexedDB（plaintext）。清 device 或者 reset
              就會抹走。要攞 key：
              <a
                href={meta.apiKeyHelpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 text-[#8AFF45] underline-offset-2 hover:underline"
              >
                {meta.label} dashboard
              </a>
              。
            </p>
          </Field>
        </div>

        <div className="mt-7 flex items-center justify-between border-t border-[#222230] pt-5">
          <button
            type="button"
            onClick={() => void onReset()}
            className="text-[11.5px] text-red-400 hover:text-red-300"
          >
            清空全部設定
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-[#222230] px-4 py-1.5 text-[12px] text-neutral-300 hover:bg-neutral-800"
            >
              取消
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="rounded-md border border-[#8AFF45] bg-[#8AFF45]/15 px-4 py-1.5 text-[12px] font-semibold text-[#8AFF45] hover:bg-[#8AFF45]/25 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? '儲存中…' : '儲存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.14em] text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}
